import { Col, createModal, ModalProps, Row, useModal, useToast } from "@commonlibs";
import useTranslate from "ui/locale/hooks/useTranslate";
import clsx from "clsx";
import Modal from "ui/common/components/feedback/Modal/Modal";
import RoleSelector from "./RoleSelector/RoleSelector";
import OperationTypeSelector from "./OperationTypeSelector/OperationTypeSelector";
import ReleaseStrategySelector from "./ReleaseStrategySelector/ReleaseStrategySelector";
import { MovementsSection, ToolbarButton, VerticalDivider } from "./ModifyMovements.styles";
import { useEffect, useState } from "react";
import { OperationType, ReleaseType, Role } from "./ModifyMovements.types";
import useGetOperationTypes from "features/release-strategy/hooks/useGetOperationTypes";
import ModifyMovementsSkeleton from "./ModifyMovementsSkeleton";
import useUpdateOperationTypes from "features/release-strategy/hooks/useUpdateOperationTypes";
import useVerifyReleaseStrategyMethod from "ui/release-strategy/hooks/useVerifyReleaseStrategyMethod";
import { Operations } from "ui/admins/operations.types";

const ModifyMovementsModal = createModal(({ className, ...rest }: ModalProps) => {
    const translate = useTranslate();
    const { hideModal } = useModal();
    const { showToast } = useToast();

    const { data, isLoading } = useGetOperationTypes();
    const { mutate, isLoading: isUpdating } = useUpdateOperationTypes({ onSuccess: onSuccessUpdate });
    const { verifyReleaseMethod, isValidatingReleaseStrategy } = useVerifyReleaseStrategyMethod({
        onSuccess: onSuccessUpdate,
        isLoading: isUpdating,
    });

    const [roles, setRoles] = useState<Role[]>();
    const [currentRole, setCurrentRole] = useState<Role>();
    const [currentOperations, setCurrentOperations] = useState<OperationType[]>([]);
    const [selectedOperation, setSelectedOperation] = useState<OperationType>();

    useEffect(() => {
        if (!data) return;
        setRoles(data);
        setCurrentRole(data.length > 0 ? data[0] : ({} as Role));
        setCurrentOperations(data.length > 0 ? data[0].operations : []);
        setSelectedOperation(data[0].operations[0]);
    }, [data, isLoading]);

    function onChangeRole(role: Role, operations: OperationType[]) {
        setCurrentRole(role);
        setCurrentOperations(operations);
        setSelectedOperation(operations[0]);
    }

    function handleOperationReleaseChange(newRelease: ReleaseType) {
        setRoles((prevRoles = []) =>
            (prevRoles ?? []).map((role) =>
                role.value === currentRole?.value
                    ? {
                          ...role,
                          operations: role.operations.map((operation) =>
                              operation.value === selectedOperation?.value ? { ...operation, release: newRelease } : operation,
                          ),
                      }
                    : role,
            ),
        );
    }

    function handleSubmit() {
        const executeAction = (...args: unknown[]) => {
            const [body] = args as [Role[]];
            mutate(body);
        };

        verifyReleaseMethod({
            originalFn: executeAction,
            originalArgs: [roles],
            operationTypeId: Operations.CREATE_AND_MODIFY_RELEASE_STRATEGY,
        });
    }

    function onSuccessUpdate() {
        showToast(translate("infoSavedSuccesfully"), { type: "success" });
        closeModal();
    }

    function closeModal() {
        hideModal(ModifyMovementsModal.id);
    }

    return (
        <Modal
            title={translate("modifyMovements")}
            className={clsx("modify-movements-modal", className)}
            style={{ minWidth: "750px", width: "auto", maxHeight: "none" }}
            {...rest}
        >
            {!isLoading && roles ? (
                <Col gap={20}>
                    <RoleSelector roles={roles} defaultRole={currentRole} onChangeRole={onChangeRole} />
                    <MovementsSection>
                        <OperationTypeSelector operations={currentOperations} setOperation={setSelectedOperation} />
                        <VerticalDivider />
                        <ReleaseStrategySelector selectedOperation={selectedOperation} onChangeRelease={handleOperationReleaseChange} />
                    </MovementsSection>
                    <Row justifyContent="end" gap={15}>
                        <ToolbarButton loading={isUpdating || isValidatingReleaseStrategy} variant="outlined" onClick={closeModal}>
                            {translate("cancel")}
                        </ToolbarButton>
                        <ToolbarButton loading={isUpdating || isValidatingReleaseStrategy} onClick={handleSubmit}>
                            {translate("save")}
                        </ToolbarButton>
                    </Row>
                </Col>
            ) : (
                <ModifyMovementsSkeleton />
            )}
        </Modal>
    );
});

export default ModifyMovementsModal;
